import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api, fmtNum, fmtDate, fmtEur, fmtMonat, aktuellerMonat } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EmptyState } from "@/components/app/EmptyState";
import { QualityBadge } from "@/components/app/QualityBadge";
import { MonthPicker } from "@/components/app/MonthPicker";
import { AbrechnungDetailDialog } from "@/pages/AbrechnungenPage";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Tractor, Loader2, Search, Eye, ShieldAlert, ShieldCheck, Ban,
  AlertTriangle, CheckCircle2, ArrowRight,
} from "lucide-react";

const ALLE = "__alle__";

const GRUND_KURZ = {
  hemmstoff: "Hemmstoff", keimzahl: "Keimzahl",
  zellzahl: "Zellzahl", manuell: "Manuell",
};

const fmtZeit = (iso) => {
  if (!iso) return "–";
  return new Date(iso).toLocaleString("de-AT", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
};

/**
 * Kompakter Qualitäts-Alert-Badge: zeigt nur an, wenn aktive Meldungen
 * (Sperren oder Verstöße im konfigurierten Zeitraum) existieren.
 */
const QualitaetAlert = ({ qualitaet, kompakt = false }) => {
  if (!qualitaet) return null;
  const {
    aktive_sperren,
    verstoesse_zeitraum, kritisch_zeitraum,
  } = qualitaet;
  if (!aktive_sperren && !verstoesse_zeitraum) return null;

  const gesperrt = aktive_sperren > 0;
  const kritisch = kritisch_zeitraum > 0;

  // Prio: Sperre > kritisch > warnung
  const variant = gesperrt || kritisch ? "destructive" : "outline";
  const klasse = !gesperrt && !kritisch
    ? "border-[hsl(var(--warning))] text-[hsl(var(--warning))]"
    : "";
  const Icon = gesperrt ? Ban : kritisch ? ShieldAlert : AlertTriangle;

  const label = gesperrt
    ? (aktive_sperren === 1 ? "1 aktive Sperre" : `${aktive_sperren} Sperren`)
    : (kritisch
      ? (kritisch_zeitraum === 1 ? "1 kritischer Verstoß" : `${kritisch_zeitraum} kritische Verstöße`)
      : (verstoesse_zeitraum === 1 ? "1 Warnung" : `${verstoesse_zeitraum} Warnungen`));

  return (
    <Badge
      data-testid="supplier-quality-alert"
      variant={variant}
      className={`inline-flex items-center gap-1 ${klasse} ${kompakt ? "text-[10px] py-0 h-4" : ""}`}
    >
      <Icon className="h-3 w-3 shrink-0" />
      {label}
    </Badge>
  );
};

/** Detail-Tab: Aktuelle &amp; historische Qualitätsmeldungen. */
const QualitaetTab = ({ liefernummer, zeitraumMonate = 3 }) => {
  const [sperren, setSperren] = useState([]);
  const [verstoesse, setVerstoesse] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const istStaff = ["admin", "sysadmin", "berater"].includes(user?.rolle);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get("/sperren", { params: { liefernummer, status: "alle" } }),
      api.get("/verstoesse", { params: { liefernummer } }),
    ]).then(([sp, vs]) => {
      setSperren(sp.data);
      setVerstoesse(vs.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [liefernummer]);

  if (loading) {
    return <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>;
  }

  const aktSperren = sperren.filter((s) => s.status === "aktiv");
  const alteSperren = sperren.filter((s) => s.status !== "aktiv");
  // Grenze anhand konfigurierbarem Zeitraum (zeitraumMonate Monate rückwirkend inkl. aktueller Monat)
  const monateNeu = verstoesse.filter((v) => {
    const heute = new Date();
    const dt = new Date(v.monat + "-01");
    const diff = (heute.getFullYear() - dt.getFullYear()) * 12 + (heute.getMonth() - dt.getMonth());
    return diff <= (zeitraumMonate - 1);
  });
  const alteVerstoesse = verstoesse.filter((v) => !monateNeu.some((n) => n.id === v.id));

  const gesamtAktiv = aktSperren.length + monateNeu.length;
  const zeitraumLabel = zeitraumMonate === 1 ? "1 Monat" : `${zeitraumMonate} Monaten`;
  const zeitraumTitel = zeitraumMonate === 1 ? "letzter Monat" : `letzte ${zeitraumMonate} Monate`;

  return (
    <div className="space-y-4">
      {/* Kopfstatus */}
      {gesamtAktiv === 0 ? (
        <div className="flex items-center gap-2 rounded-md bg-[hsl(var(--success))]/10 border border-[hsl(var(--success))]/40 px-3 py-2 text-sm">
          <CheckCircle2 className="h-4 w-4 text-[hsl(var(--success))] shrink-0" />
          <span>Aktuell keine aktiven Meldungen für diesen Betrieb.</span>
        </div>
      ) : (
        <div className="flex items-start gap-2 rounded-md bg-destructive/5 border border-destructive/40 px-3 py-2 text-sm">
          <ShieldAlert className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
          <div className="flex-1">
            <p className="font-medium text-destructive">
              {aktSperren.length > 0 && `${aktSperren.length} aktive Sperre(n)`}
              {aktSperren.length > 0 && monateNeu.length > 0 && " · "}
              {monateNeu.length > 0 && `${monateNeu.length} Verstoß/Verstöße in den letzten ${zeitraumLabel}`}
            </p>
          </div>
          {istStaff && (
            <Button asChild variant="outline" size="sm" data-testid="supplier-quality-goto-admin">
              <Link to={`/admin/qualitaet?tab=sperren&liefernummer=${liefernummer}`}>
                Verwalten <ArrowRight className="h-3.5 w-3.5 ml-1" />
              </Link>
            </Button>
          )}
        </div>
      )}

      {/* Aktive Sperren */}
      {aktSperren.length > 0 && (
        <section>
          <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Aktive Sperren</p>
          <div className="space-y-2">
            {aktSperren.map((s) => (
              <div key={s.id} className="rounded-md border border-destructive/40 bg-destructive/5 p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <Badge variant="destructive">{GRUND_KURZ[s.grund] || s.grund}</Badge>
                    {s.automatisch && <span className="text-[10px] uppercase text-muted-foreground">automatisch</span>}
                  </div>
                  <span className="text-xs text-muted-foreground">seit {fmtZeit(s.erstellt_am)}</span>
                </div>
                {s.details && <p className="text-sm mt-1.5">{s.details}</p>}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Verstöße letzte 3 Monate */}
      {monateNeu.length > 0 && (
        <section>
          <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Verstöße ({zeitraumTitel})</p>
          <div className="rounded-md border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Monat</TableHead>
                  <TableHead>Typ</TableHead>
                  <TableHead>Schwere</TableHead>
                  <TableHead>Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {monateNeu.map((v) => (
                  <TableRow key={v.id}>
                    <TableCell className="font-medium whitespace-nowrap">{fmtMonat(v.monat)}</TableCell>
                    <TableCell className="text-sm whitespace-nowrap">{v.typ}</TableCell>
                    <TableCell>
                      <Badge
                        variant={v.schwere === "kritisch" ? "destructive" : "outline"}
                        className={v.schwere === "warnung" ? "border-[hsl(var(--warning))] text-[hsl(var(--warning))]" : ""}
                      >
                        {v.schwere}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-[320px]">{v.details}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </section>
      )}

      {/* Historisch */}
      {(alteSperren.length > 0 || alteVerstoesse.length > 0) && (
        <section>
          <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Historie</p>
          <div className="rounded-md border overflow-x-auto max-h-64 overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Zeitraum</TableHead>
                  <TableHead>Kategorie</TableHead>
                  <TableHead>Status / Schwere</TableHead>
                  <TableHead>Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {alteSperren.map((s) => (
                  <TableRow key={s.id}>
                    <TableCell className="text-xs whitespace-nowrap">
                      {fmtZeit(s.erstellt_am)}
                      {s.aufgehoben_am && <><br /><span className="text-muted-foreground">bis {fmtZeit(s.aufgehoben_am)}</span></>}
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">Sperre · {GRUND_KURZ[s.grund] || s.grund}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">{s.status}</Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-[320px]">
                      {s.details}
                      {s.aufgehoben_kommentar && (
                        <p className="italic mt-1">&bdquo;{s.aufgehoben_kommentar}&ldquo; &ndash; {s.aufgehoben_von}</p>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {alteVerstoesse.map((v) => (
                  <TableRow key={v.id}>
                    <TableCell className="text-xs whitespace-nowrap">{fmtMonat(v.monat)}</TableCell>
                    <TableCell><Badge variant="outline">Verstoß · {v.typ}</Badge></TableCell>
                    <TableCell>
                      <Badge
                        variant={v.schwere === "kritisch" ? "destructive" : "outline"}
                        className={v.schwere === "warnung" ? "border-[hsl(var(--warning))] text-[hsl(var(--warning))]" : ""}
                      >
                        {v.schwere}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-[320px]">{v.details}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </section>
      )}
    </div>
  );
};

const LieferantDetailDialog = ({ lieferant, defaultTab = "lieferungen", onClose }) => {
  const [monat, setMonat] = useState(aktuellerMonat());
  const [lieferungen, setLieferungen] = useState([]);
  const [proben, setProben] = useState([]);
  const [abrechnungen, setAbrechnungen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [abDetail, setAbDetail] = useState(null);

  useEffect(() => {
    if (!lieferant) return;
    setLoading(true);
    Promise.all([
      api.get("/lieferungen", { params: { liefernummer: lieferant.liefernummer, monat } }),
      api.get("/laborwerte", { params: { liefernummer: lieferant.liefernummer, monat } }),
      api.get("/abrechnungen", { params: { liefernummer: lieferant.liefernummer } }),
    ])
      .then(([l, p, a]) => {
        setLieferungen(l.data);
        setProben(p.data);
        setAbrechnungen(a.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [lieferant, monat]);

  const summe = lieferungen.reduce((acc, r) => acc + (r.menge_kg || 0), 0);
  const hatMeldung = lieferant?.qualitaet?.aktive_sperren > 0 || lieferant?.qualitaet?.verstoesse_zeitraum > 0;
  const zeitraumMonate = lieferant?.qualitaet?.zeitraum_monate || 3;

  return (
    <>
      <Dialog open={!!lieferant} onOpenChange={(o) => !o && onClose()}>
        <DialogContent
          data-testid="supplier-detail-dialog"
          className="max-w-3xl max-h-[92vh] overflow-y-auto"
        >
          <DialogHeader>
            <DialogTitle className="flex flex-wrap items-center gap-2">
              <span>{lieferant?.name} – {lieferant?.hofname}</span>
              <QualitaetAlert qualitaet={lieferant?.qualitaet} />
            </DialogTitle>
            <DialogDescription>
              Liefernr. {lieferant?.liefernummer} · {lieferant?.milchsorte} · {lieferant?.gruppe} · {lieferant?.plz} {lieferant?.ort}
            </DialogDescription>
          </DialogHeader>

          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : (
            <Tabs defaultValue={defaultTab}>
              <TabsList className="w-full h-auto p-1 overflow-x-auto">
                <TabsTrigger data-testid="supplier-tab-deliveries" value="lieferungen" className="flex-1">
                  <span className="hidden sm:inline">Lieferungen&nbsp;</span>
                  <span className="sm:hidden">Lief. </span>
                  ({lieferungen.length})
                </TabsTrigger>
                <TabsTrigger data-testid="supplier-tab-samples" value="proben" className="flex-1">
                  Proben ({proben.length})
                </TabsTrigger>
                <TabsTrigger data-testid="supplier-tab-settlements" value="abrechnungen" className="flex-1">
                  <span className="hidden sm:inline">Abrechnungen&nbsp;</span>
                  <span className="sm:hidden">Abr. </span>
                  ({abrechnungen.length})
                </TabsTrigger>
                <TabsTrigger data-testid="supplier-tab-quality" value="qualitaet" className="flex-1 relative">
                  Qualität
                  {hatMeldung && (
                    <span className="ml-1.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-medium text-destructive-foreground font-num">
                      {(lieferant.qualitaet.aktive_sperren + lieferant.qualitaet.verstoesse_zeitraum)}
                    </span>
                  )}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="lieferungen" className="mt-4">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                  <p className="text-sm text-muted-foreground">Summe: <span className="font-num font-semibold text-foreground">{fmtNum(summe)} kg</span></p>
                  <MonthPicker value={monat} onChange={setMonat} testId="supplier-detail-month-select" />
                </div>
                <div className="rounded-lg border overflow-x-auto max-h-72 overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Datum</TableHead>
                        <TableHead className="text-right">Menge (kg)</TableHead>
                        <TableHead>Probe</TableHead>
                        <TableHead>Tour</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {lieferungen.map((r) => (
                        <TableRow key={r.id}>
                          <TableCell>{fmtDate(r.datum)}</TableCell>
                          <TableCell className="text-right font-num">{fmtNum(r.menge_kg)}</TableCell>
                          <TableCell>{r.probe_gezogen ? <Badge variant="secondary">Probe</Badge> : "–"}</TableCell>
                          <TableCell>{r.tour}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </TabsContent>

              <TabsContent value="proben" className="mt-4">
                <div className="rounded-lg border overflow-x-auto max-h-72 overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Datum</TableHead>
                        <TableHead className="text-right">Fett %</TableHead>
                        <TableHead className="text-right">Eiweiß %</TableHead>
                        <TableHead className="text-right">Zellzahl</TableHead>
                        <TableHead className="text-right">Keimzahl</TableHead>
                        <TableHead>Hemmstoff</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {proben.map((p) => (
                        <TableRow key={p.id}>
                          <TableCell>{fmtDate(p.datum)}</TableCell>
                          <TableCell className="text-right font-num">{fmtNum(p.fett, 2)}</TableCell>
                          <TableCell className="text-right font-num">{fmtNum(p.eiweiss, 2)}</TableCell>
                          <TableCell className="text-right font-num">{fmtNum(p.zellzahl)}</TableCell>
                          <TableCell className="text-right font-num">{fmtNum(p.keimzahl)}</TableCell>
                          <TableCell>{p.hemmstoff ? <Badge variant="destructive">positiv</Badge> : "negativ"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </TabsContent>

              <TabsContent value="abrechnungen" className="mt-4">
                <div className="rounded-lg border overflow-x-auto max-h-72 overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Monat</TableHead>
                        <TableHead className="text-right">Menge (kg)</TableHead>
                        <TableHead>Qualität</TableHead>
                        <TableHead className="text-right">Brutto</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {abrechnungen.map((a) => (
                        <TableRow key={a.id}>
                          <TableCell>{fmtMonat(a.monat)}</TableCell>
                          <TableCell className="text-right font-num">{fmtNum(a.menge_kg)}</TableCell>
                          <TableCell><QualityBadge klasse={a.qualitaetsklasse} icon={a.qualitaetsklasse_icon} farbe={a.qualitaetsklasse_farbe} /></TableCell>
                          <TableCell className="text-right font-num font-semibold">{fmtEur(a.brutto_eur)}</TableCell>
                          <TableCell>
                            <Button variant="ghost" size="icon" onClick={() => setAbDetail(a)}><Eye className="h-4 w-4" /></Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </TabsContent>

              <TabsContent value="qualitaet" className="mt-4">
                <QualitaetTab liefernummer={lieferant?.liefernummer} zeitraumMonate={zeitraumMonate} />
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>
      <AbrechnungDetailDialog abrechnung={abDetail} onClose={() => setAbDetail(null)} />
    </>
  );
};

export default function LieferantenPage() {
  const [rows, setRows] = useState([]);
  const [gruppen, setGruppen] = useState([]);
  const [sorten, setSorten] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gruppe, setGruppe] = useState(ALLE);
  const [sorte, setSorte] = useState(ALLE);
  const [suche, setSuche] = useState("");
  const [detail, setDetail] = useState(null);
  const [detailTab, setDetailTab] = useState("lieferungen");
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    Promise.all([api.get("/gruppen"), api.get("/milchsorten")])
      .then(([g, s]) => { setGruppen(g.data); setSorten(s.data); })
      .catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (gruppe !== ALLE) params.gruppe_id = gruppe;
    if (sorte !== ALLE) params.milchsorte_id = sorte;
    if (suche) params.suche = suche;
    api.get("/lieferanten", { params })
      .then((res) => setRows(res.data))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [gruppe, sorte, suche]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  // Deep-Link Support: `?liefernummer=100301` öffnet den Detail-Dialog automatisch
  // auf dem "Qualität"-Tab, damit die Verlinkung vom Dashboard direkt sinnvoll ist.
  useEffect(() => {
    const paramLief = searchParams.get("liefernummer");
    if (!paramLief || !rows.length || detail) return;
    const treffer = rows.find((r) => r.liefernummer === paramLief);
    if (treffer) {
      setDetailTab("qualitaet");
      setDetail(treffer);
      // URL-Parameter danach zurücksetzen, damit ein Schließen nicht sofort neu öffnet
      searchParams.delete("liefernummer");
      setSearchParams(searchParams, { replace: true });
    }
  }, [rows, searchParams, setSearchParams, detail]);

  const anzahlMitMeldung = useMemo(
    () => rows.filter((r) => r.qualitaet?.aktive_sperren > 0 || r.qualitaet?.verstoesse_zeitraum > 0).length,
    [rows]
  );

  const oeffneDetail = (r, tab = "lieferungen") => {
    setDetailTab(tab);
    setDetail(r);
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-semibold">Lieferanten</h1>
        <p className="text-sm text-muted-foreground">
          {fmtNum(rows.length)} Betriebe
          {anzahlMitMeldung > 0 && (
            <> · <span className="text-destructive font-medium">{anzahlMitMeldung} mit aktiven Meldungen</span></>
          )}
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[180px] sm:flex-none">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            data-testid="suppliers-search-input"
            className="pl-8 sm:w-[240px] bg-card"
            placeholder="Name, Hof, Liefernr. …"
            value={suche}
            onChange={(e) => setSuche(e.target.value)}
          />
        </div>
        <Select value={gruppe} onValueChange={setGruppe}>
          <SelectTrigger data-testid="suppliers-group-filter" className="flex-1 sm:flex-none sm:w-[180px] bg-card">
            <SelectValue placeholder="Gruppe" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALLE}>Alle Gruppen</SelectItem>
            {gruppen.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={sorte} onValueChange={setSorte}>
          <SelectTrigger data-testid="suppliers-milktype-filter" className="flex-1 sm:flex-none sm:w-[180px] bg-card">
            <SelectValue placeholder="Milchsorte" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALLE}>Alle Milchsorten</SelectItem>
            {sorten.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : rows.length ? (
            <>
              {/* Mobile: Karten-Layout (< md) */}
              <div className="md:hidden divide-y" data-testid="suppliers-mobile-list">
                {rows.map((r) => (
                  <button
                    key={r.id}
                    onClick={() => oeffneDetail(r)}
                    data-testid={`supplier-mobile-card-${r.liefernummer}`}
                    className="w-full text-left p-3 space-y-1.5 hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-colors"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-num font-medium">{r.liefernummer}</span>
                          <span className="font-medium truncate">{r.name}</span>
                        </div>
                        <p className="text-xs text-muted-foreground truncate">
                          {r.hofname && <>{r.hofname} · </>}
                          {r.plz} {r.ort}
                        </p>
                      </div>
                      <Eye className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5">
                      <Badge variant="secondary" className="text-[10px] py-0 h-4">{r.milchsorte}</Badge>
                      {r.gruppe && r.gruppe !== "-" && (
                        <Badge variant="outline" className="text-[10px] py-0 h-4">{r.gruppe}</Badge>
                      )}
                      {!r.aktiv && (
                        <Badge variant="outline" className="text-[10px] py-0 h-4">inaktiv</Badge>
                      )}
                      <QualitaetAlert qualitaet={r.qualitaet} kompakt />
                    </div>
                  </button>
                ))}
              </div>

              {/* Desktop: Tabelle (>= md) */}
              <div className="hidden md:block overflow-x-auto">
                <Table data-testid="suppliers-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="sticky left-0 bg-card">Liefernr.</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Hof</TableHead>
                      <TableHead>Ort</TableHead>
                      <TableHead>Milchsorte</TableHead>
                      <TableHead>Gruppe</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Meldung</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((r) => {
                      const hatMeldung = r.qualitaet?.aktive_sperren > 0 || r.qualitaet?.verstoesse_zeitraum > 0;
                      return (
                        <TableRow
                          key={r.id}
                          className="cursor-pointer hover:bg-muted/50"
                          onClick={() => oeffneDetail(r)}
                        >
                          <TableCell className="sticky left-0 bg-card font-num font-medium">{r.liefernummer}</TableCell>
                          <TableCell>{r.name}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">{r.hofname}</TableCell>
                          <TableCell className="text-sm">{r.plz} {r.ort}</TableCell>
                          <TableCell><Badge variant="secondary">{r.milchsorte}</Badge></TableCell>
                          <TableCell className="text-sm">{r.gruppe}</TableCell>
                          <TableCell>
                            {r.aktiv
                              ? <Badge variant="outline" className="text-[hsl(var(--success))] border-[hsl(var(--success))]">aktiv</Badge>
                              : <Badge variant="outline">inaktiv</Badge>}
                          </TableCell>
                          <TableCell>
                            {hatMeldung ? (
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); oeffneDetail(r, "qualitaet"); }}
                                data-testid={`supplier-quality-link-${r.liefernummer}`}
                                className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
                              >
                                <QualitaetAlert qualitaet={r.qualitaet} />
                              </button>
                            ) : (
                              <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
                                <ShieldCheck className="h-3 w-3 text-[hsl(var(--success))]" />
                                OK
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Button
                              data-testid={`supplier-detail-button-${r.liefernummer}`}
                              variant="ghost" size="icon"
                              onClick={(e) => { e.stopPropagation(); oeffneDetail(r); }}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </>
          ) : (
            <EmptyState icon={Tractor} title="Keine Lieferanten gefunden." text="Passen Sie die Filter an." />
          )}
        </CardContent>
      </Card>

      {detail && (
        <LieferantDetailDialog
          lieferant={detail}
          defaultTab={detailTab}
          onClose={() => setDetail(null)}
        />
      )}
    </div>
  );
}
